
import Vue from 'vue';

// Assets
import Logo from '~/assets/img/logo.svg';

// Services & types
import CandidateService, { Candidate, WorkAuthorization } from '~/services/CandidateService';
import { User } from 'talkjs/all';
import { CandidateStudyProgram } from '~/services/types/csp';
import {
    ImportSection,
    ProfileImportResult,
    Section,
    SectionType,
    SourceType,
} from '~/services/types/import';
import {
    anyItemNeedAttention,
    anySectionNeedAttention,
    getImportErrorDescription,
    getImportTypeDescription,
} from '~/utils/candidate/ProfileImportHelper';
import DialogService from '~/services/DialogService';
import { format } from '~/utils/date-fns';
import { parseISO } from 'date-fns';

// Components
import Button from '~/components/builders/Base/Button.vue';
import Modal from '~/components/builders/Base/Modal.vue';
import Input from '~/components/builders/Base/Input.vue';
import Checkbox from '~/components/builders/Base/Checkbox.vue';
import LanguagesModal from '~/components/profile/modals/LanguagesModal.vue';
import StudyProgramsModal from '../modals/StudyProgramsModal.vue';
import PersonalDetailsModal from '../modals/PersonalDetailsModal.vue';
import MembershipModal from '../modals/MembershipModal.vue';
import WorkExperienceModal from '../modals/WorkExperienceModal.vue';
import ImportBox from '~/components/profile/components/ImportBox.vue';
import SummaryModal from '~/components/profile/modals/SummaryModal.vue';
import { MessageState } from '~/store/customModules/messageModule';

export enum ImportMode {
    EnterId = 'EnterId',
    SelectWhatToImport = 'SelectWhatToImport',
    SelectWhatToUpload = 'SelectWhatToUpload',
    InProgress = 'InProgress',
    Results = 'Results',
    FileInput = 'FileInput',
}

type SectionItem = {
    id: SectionType;
    selected: boolean;
    visible: boolean;
    records: [];
    isAction: false;
};

export default Vue.extend({
    name: 'ProfileImportModal',
    components: {
        Modal,
        Input,
        Button,
        Checkbox,
        LanguagesModal,
        StudyProgramsModal,
        PersonalDetailsModal,
        MembershipModal,
        WorkExperienceModal,
        ImportBox,
        SummaryModal,
    },
    data() {
        return {
            candidateService: new CandidateService(),

            Logo,

            state: ImportMode.EnterId,

            file: null as File | null,

            link: '',
            show: false,

            importFailedError: null as string | null,

            isExpanded: false,

            showBasicModal: false,
            showLanguagesModal: false,
            showEducationModal: false,
            showWorkExperienceModal: false,
            showMembershipModal: false,
            showSummaryModal: false,

            Sections: [
                { id: SectionType.Overview },
                { id: SectionType.Summary },
                { id: SectionType.Languages },
                { id: SectionType.Education },
                { id: SectionType.WorkExperience },
                { id: SectionType.Memberships },
            ] as SectionItem[],
        };
    },
    computed: {
        resultViewOnlyMode(): boolean {
            return this.state === ImportMode.Results && !!this.lastProfileImport?.reviewedAt;
        },
        sourceType(): string | null {
            if (this.lastProfileImport === null) return null;
            switch (this.lastProfileImport.sourceType) {
                case SourceType.LinkedIn:
                    return String(this.$t('profile.import.importPopup.sourceTypeLinkedIn'));
                case SourceType.CV:
                    return String(this.$t('profile.import.importPopup.sourceTypeCV'));
                default:
                    return null;
            }
        },
        importedAt(): string | null {
            if (this.lastProfileImport === null) return null;
            return format(parseISO(this.lastProfileImport.createdAt.toString()), 'dd.MM.yyyy');
        },
        lastProfileImport(): ProfileImportResult | null {
            return this.$store.getters['profileModule/getLastProfileImport']();
        },
        user(): User | null {
            return this.$store.getters['profileModule/getUser']();
        },
        candidate(): Candidate | null {
            return this.$store.getters['profileModule/getCandidate']();
        },
        workAuthorizations(): WorkAuthorization[] | null {
            return this.$store.getters['profileModule/getWorkAuthorizations']();
        },
        studyPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getSortedStudyPrograms']();
        },
        canGoNext(): boolean {
            switch (this.state) {
                case ImportMode.EnterId:
                    return this.linkValid;
                case ImportMode.FileInput:
                    return this.fileValid;
                case ImportMode.SelectWhatToImport:
                    return this.linkValid && this.importSectionsSelectionValid;
                case ImportMode.SelectWhatToUpload:
                    return this.fileValid && this.importSectionsSelectionValid;
                default:
                    return true;
            }
        },
        linkValid(): boolean {
            return new RegExp('https?://(?:www.)?linkedin.com/in/.+').test(this.link.trim());
        },
        fileValid(): boolean {
            return Boolean(
                this.file?.type.match(
                    'application/pdf|application/vnd.openxmlformats-officedocument.wordprocessingml.document|application/msword'
                ) ?? false
            );
        },
        importSectionsSelectionValid(): boolean {
            return this.Sections.some(x => x.selected);
        },
        anySectionNeedAttention(): boolean {
            return (
                this.lastProfileImport != null &&
                anySectionNeedAttention(this.lastProfileImport.result.sections)
            );
        },
    },
    methods: {
        uploadClicked() {
            (this.$refs['fileInput'] as any).click();
        },
        async onFileChange(event: Event) {
            if (!event.target) {
                return;
            }

            const target = event.target as HTMLInputElement;

            if (!target.files || !target.files.length) {
                return;
            }

            this.file = target.files[0];

            if (!this.fileValid) {
                (this as any).$interfaces.messageModule.addNewMessage({
                    title: this.$t('spotlight.personalDetails.wrongFileTypeErrorHeader'),
                    message: this.$t('documents.cv.fileFormatInfo'),
                    state: MessageState.ERROR,
                    duration: 6000,
                });
                this.file = null;
                return;
            }

            if (this.file.size > 16 * 1024 * 1024) {
                (this as any).$interfaces.messageModule.addNewMessage({
                    title: this.$t('documents.cv.wrongFileSizeHeader'),
                    message: this.$t('documents.cv.wrongFIleSizeDescription'),
                    state: MessageState.ERROR,
                    duration: 6000,
                });
                this.file = null;
                return;
            }
        },
        onSectionSelected(item: SectionItem) {
            item.selected = !item.selected;
        },
        sectionNeedAttention(value: Section): boolean {
            return anyItemNeedAttention(value.items);
        },
        sectionNameByType(type: string): string {
            return getImportTypeDescription(type);
        },
        getImportSection(sectionType: string): ImportSection | undefined {
            if (!this.lastProfileImport) return undefined;

            const section = this.lastProfileImport.result.sections.find(
                x => x.sectionType === sectionType
            );
            if (!section) return undefined;

            return {
                id: this.lastProfileImport.id,
                createdAt: this.lastProfileImport.createdAt,
                sourceType: this.lastProfileImport.sourceType,
                section: section,
            };
        },
        handleShortCutSection(sectionType: string) {
            switch (sectionType) {
                case SectionType.Overview:
                    this.showBasicModal = true;
                    break;
                case SectionType.Languages:
                    this.showLanguagesModal = true;
                    break;
                case SectionType.Education:
                    this.showEducationModal = true;
                    break;
                case SectionType.WorkExperience:
                    this.showWorkExperienceModal = true;
                    break;
                case SectionType.Memberships:
                    this.showMembershipModal = true;
                    break;
                case SectionType.Summary:
                    this.showSummaryModal = true;
                    break;
            }
        },
        finishReview() {
            if (!this.lastProfileImport) return;

            this.candidateService
                .updateLastProfileImportReviewed(this.lastProfileImport.id, true)
                .finally(() => window.dispatchEvent(new CustomEvent('candidate:profile:update')));
        },
        handleNext() {
            this.importFailedError = null;

            switch (this.state) {
                case ImportMode.EnterId:
                    this.state = ImportMode.SelectWhatToImport;
                    break;
                case ImportMode.FileInput:
                    this.state = ImportMode.SelectWhatToUpload;
                    break;
                case ImportMode.SelectWhatToUpload:
                    this.state = ImportMode.InProgress;

                    this.candidateService
                        .importCV(
                            this.file,
                            this.Sections.filter(x => x.selected).map(
                                ({ id }) => id
                            ) as SectionType[]
                        )
                        .then(importResult => {
                            if (importResult) {
                                window.dispatchEvent(new CustomEvent('candidate:profile:update'));
                                this.state = ImportMode.Results;
                            } else {
                                this.importFailedError = getImportErrorDescription(importResult);
                                this.state = ImportMode.SelectWhatToUpload;
                            }
                        })
                        .catch(error => {
                            this.importFailedError = getImportErrorDescription(error);
                            this.state = ImportMode.SelectWhatToUpload;
                        });
                    break;
                case ImportMode.SelectWhatToImport:
                    this.state = ImportMode.InProgress;

                    this.candidateService
                        .importProfile(
                            this.link,
                            this.Sections.filter(x => x.selected).map(
                                ({ id }) => id
                            ) as SectionType[]
                        )
                        .then(importResult => {
                            if (importResult) {
                                window.dispatchEvent(new CustomEvent('candidate:profile:update'));
                                this.state = ImportMode.Results;
                            } else {
                                this.importFailedError = getImportErrorDescription(importResult);
                                this.state = ImportMode.SelectWhatToImport;
                            }
                        })
                        .catch(error => {
                            this.importFailedError = getImportErrorDescription(error);
                            this.state = ImportMode.SelectWhatToImport;
                        });
                    break;
                case ImportMode.Results:
                    if (this.anySectionNeedAttention) {
                        this.askFinishImportReview();
                    } else {
                        this.finishReview();
                        this.handleClose();
                    }
                    break;
            }
        },
        handleClose() {
            this.$emit('close');
            this.show = false;
        },
        isSectionVisible(type: SectionType): boolean {
            return type !== SectionType.Summary || this.state !== ImportMode.FileInput;
        },
        handleShow(event: any) {
            this.state = event.detail;
            this.importFailedError = null;
            this.Sections.forEach(x => (x.visible = this.isSectionVisible(x.id)));
            this.Sections.forEach(x => (x.selected = x.visible));
            this.link = '';
            this.file = null;
            this.show = true;
        },
        askFinishImportReview() {
            const executeAndCloseAction = ({ res }: { res: (s: string) => void }) => {
                res('close');
                this.finishReview();
                this.handleClose();
            };
            const closeAction = ({ res }: { res: (s: string) => void }) => {
                res('close');
            };

            DialogService.openDialog({
                title: String(this.$t('profile.import.finishReviewConfirm.title')),
                contentHTML: String(this.$t('profile.import.finishReviewConfirm.text')),
                actions: [
                    {
                        title: String(this.$t('general.finish')),
                        variant: 'outline-primary',
                        action: executeAndCloseAction,
                    },
                    {
                        title: String(this.$t('general.cancel')),
                        variant: 'outline-secondary',
                        action: closeAction,
                    },
                ],
            });
        },
    },
    watch: {
        linkValid(value: boolean) {
            if (value && this.state === ImportMode.EnterId) {
                this.state = ImportMode.SelectWhatToImport;
            }
        },
        fileValid(value: boolean) {
            if (value && this.state === ImportMode.FileInput) {
                this.state = ImportMode.SelectWhatToUpload;
            }
        },
    },
    mounted() {
        window.addEventListener('profileimport:modal:show', this.handleShow);

        this.$root.$on('bv::collapse::state', (id: any, collapsed: any) => {
            const el = document.getElementById(id + '_chevron');
            if (el) {
                if (collapsed) el.classList.remove('rotated');
                else el.classList.add('rotated');
            }
        });
    },
    beforeDestroy() {
        window.removeEventListener('profileimport:modal:show', this.handleShow);
        this.$root.$off('bv::collapse::state');
    },
});
